export type TargetApplicationID = keyof typeof TARGET_APPLICATIONS;

/**
 * Specifies where the app will be hosted, e.g. set to "/login" if the app will be hosted on
 * https://dev.int.nl-ix.net/login and should parse and route pages under that path instead of from the root.
 */
export const ROUTING_BASENAME = process.env.REACT_APP_ROUTING_BASENAME;

/**
 * Specifies the NLIX-API base URL to use. Defaults to https://api.nl-ix.net if not set.
 */
export const NLIX_API_URL = process.env.REACT_APP_NLIX_API_URL || "https://api.nl-ix.net";

export const TARGET_APPLICATIONS = {
    mynlix: {
        name: "MyNL-ix",
        url: process.env.REACT_APP_TARGET_MYNLIX_URL as string,
    },
    rsc: {
        name: "Route Server Configurator",
        url: process.env.REACT_APP_TARGET_RSC_URL as string,
    },
    "elastic-interconnect": {
        name: "Elastic Interconnect",
        url: process.env.REACT_APP_TARGET_ELASTIC_URL as string,
    },
    "icon": {
        name: "Icon",
        url: process.env.REACT_APP_TARGET_ELASTIC_URL as string,
    },
};

export const DEFAULT_TARGET_APPLICATION: TargetApplicationID = "mynlix";

/**
 * Combines a target application's base URL with an optional query parameter path, joining
 * them with exactly one slash. Returns the base URL unchanged when no path is
 * given.
 *
 * Any path that resolves outside the application — an absolute or
 * scheme-relative URL, a non-http scheme, or a `..` traversal that climbs out of
 * the application's base path/origin — is ignored, falling back to the base URL.
 * This stops a crafted `?path` from redirecting the user elsewhere.
 *
 * @example
 *   buildApplicationUrl("https://account.nl-ix.net/mynlix", "/#/100/organization")
 *   // => "https://account.nl-ix.net/mynlix/#/100/organization"
 *   buildApplicationUrl("https://account.nl-ix.net/mynlix", "https://evil.com")
 *   // => "https://account.nl-ix.net/mynlix"  (ignored)
 */
export function buildApplicationUrl(baseUrl: string, path?: string | null): string {
    // No path parameter, use the base url
    if (!path) {
        return baseUrl;
    }

    // Browsers treat backslashes as forward slashes, so normalise them first to
    // stop `/\evil.com` style bypasses.
    const normalisedPath = path.replace(/\\/g, "/");

    // Reject anything that isn't a plain in-app path. Absolute URLs
    // (`https://…`), scheme-relative URLs (`//evil.com`) and other schemes
    // (`javascript:…`, `data:…`) all resolve outside the application and are
    // ignored in favour of the base URL.
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(normalisedPath);
    const isSchemeRelative = normalisedPath.startsWith("//");

    if (hasScheme || isSchemeRelative) {
        return baseUrl;
    }

    const base = baseUrl.replace(/\/+$/, "");
    const suffix = normalisedPath.startsWith("/") ? normalisedPath : `/${normalisedPath}`;
    const candidate = `${base}${suffix}`;

    // Defence in depth: once the browser resolves `..` segments the result must
    // still sit on the same origin and within the application's base path. If it
    // escapes (e.g. `/../../admin` → `/admin`), fall back to the base URL.
    try {
        const baseObject = new URL(baseUrl);
        const candidateObject = new URL(candidate);
        const basePath = baseObject.pathname.replace(/\/+$/, "");

        const sameOrigin = candidateObject.origin === baseObject.origin;
        const withinBasePath =
            basePath === "" ||
            candidateObject.pathname === basePath ||
            candidateObject.pathname.startsWith(`${basePath}/`);

        if (!sameOrigin || !withinBasePath) {
            return baseUrl;
        }
    } catch {
        return baseUrl;
    }

    return candidate;
}
